import { closeInappSelect, syncSelect } from './inapp-select.js';

// This small editor creates a shared property definition independently of the event form.
export function bindPropertyCreate(button, panel, onCreate) {
    if (!button || !panel) return null;
    const name = panel.querySelector('[name="my_prop_name"]');
    const type = panel.querySelector('[name="my_prop_type"]');
    const submit = panel.querySelector('#calMyPropAddBtn');
    const hint = panel.querySelector('#calMyPropTypeHint');
    const error = panel.querySelector('#calMyPropError');
    const cancel = panel.querySelectorAll('[data-prop-cancel]');
    const typeButton = type.closest('.cal-sel').querySelector('.cal-sel-btn');
    typeButton.setAttribute('aria-labelledby', 'calMyPropTypeLabel');
    typeButton.setAttribute('aria-describedby', hint.id);
    panel.querySelector('#calMyPropTypeLabel').addEventListener('click', () => typeButton.focus());
    let pending = false;

    function sync() {
        submit.disabled = pending || !name.value.trim();
        const label = pending ? submit.dataset.busy : submit.dataset.label;
        // A blur/change can run between pointerdown and click. Keep the button's
        // children stable unless the label changed, so the click is not cancelled.
        if (submit.textContent !== label) submit.textContent = label;
        name.disabled = type.disabled = pending;
        cancel.forEach((b) => { b.disabled = pending; });
        button.disabled = pending;
        panel.setAttribute('aria-busy', String(pending));
        hint.textContent = type.selectedOptions[0]?.dataset.description || '';
        syncSelect(type);
    }
    function reset(focus = false) {
        if (!panel.hidden) closeInappSelect();
        panel.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        name.value = '';
        type.value = 'text';
        error.hidden = true;
        error.textContent = '';
        sync();
        if (focus) button.focus();
    }
    button.addEventListener('click', () => {
        if (!panel.hidden) { reset(true); return; }
        panel.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        sync();
        name.focus();
    });
    cancel.forEach((b) => b.addEventListener('click', () => reset(true)));
    // Editing this draft must not dirty or submit the surrounding event form.
    panel.addEventListener('input', (e) => { e.stopPropagation(); error.hidden = true; sync(); });
    panel.addEventListener('change', (e) => { e.stopPropagation(); sync(); });

    async function create() {
        if (pending || !name.value.trim()) { name.focus(); return; }
        const body = { name: name.value.trim(), type: type.value };
        if (type.value === 'relation') body.config = { target: 'me' };
        pending = true;
        error.hidden = true;
        sync();
        try {
            const response = await (window.csrfFetch || fetch)('/calendar/me/properties', {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(body),
            });
            if (!response.ok) throw new Error('create property failed');
            const property = await response.json();
            pending = false;
            reset();
            onCreate(property);
        } catch (_) {
            // Keep the entered name/type intact so retry never means starting over.
            error.textContent = panel.dataset.failed;
            error.hidden = false;
        } finally {
            pending = false;
            sync();
        }
    }
    submit.addEventListener('click', create);
    name.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); create(); }
    });
    panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            if (!pending) reset(true);
        }
    });
    sync();
    return { reset, close: () => { if (!pending) reset(true); } };
}
